import type { ReactNode } from 'react';

import {
  DataTableColumnHeader,
  type DataTableFeatures,
  type DataTableRowPresentation,
  DataTableVirtualized,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from 'design-system';
import { InfoIcon } from 'lucide-react';

import type { Header, ReactTable, Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { COLLAPSED_ROW_HEIGHT, VIEW_IN_SPOT_LINK_HEIGHT } from '../constants';
import PeriodDetailPanel from '../PeriodDetailPanel/PeriodDetailPanel';
import ViewInOtherCatalogLink from '../ViewInOtherCatalogLink/ViewInOtherCatalogLink';

interface CatalogTableGridProps {
  table: ReactTable<DataTableFeatures, CatalogItem>;
  rows: Row<DataTableFeatures, CatalogItem>[];
  isForwardCurves: boolean;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  periodGroups: Map<string, CatalogItem[]>;
  fullyExpandedCodes: Set<string>;
  getRowPresentation: (row: Row<DataTableFeatures, CatalogItem>) => DataTableRowPresentation;
  onRowActivate: (row: Row<DataTableFeatures, CatalogItem>) => void;
  onShowAll: (code: string) => void;
  onViewInOtherCatalog?: (code: string) => void;
  hasCodeFilter?: boolean;
}

const CatalogTableGrid = ({
  table,
  rows,
  isForwardCurves,
  isInDashboard,
  renderFactsheetButton,
  periodGroups,
  fullyExpandedCodes,
  getRowPresentation,
  onRowActivate,
  onShowAll,
  onViewInOtherCatalog,
  hasCodeFilter
}: CatalogTableGridProps) => {
  const renderHeader = (header: Header<DataTableFeatures, CatalogItem>) => {
    const headerLabel = header.column.columnDef.header;
    if (headerLabel === 'AdditionalItem') return null;

    const content = header.column.getCanSort() ? (
      <DataTableColumnHeader
        column={header.column}
        title={typeof headerLabel === 'string' ? headerLabel : ''}
      />
    ) : (
      flexRender(headerLabel, header.getContext())
    );

    return headerLabel === 'Info' ? (
      <span className='inline-flex items-center gap-1'>
        {content}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  aria-label='About index information'
                  className='inline-flex items-center'
                >
                  <InfoIcon className='size-3' />
                </span>
              }
            />
            <TooltipContent>
              Index information pages provide details of the methodology behind the index.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>
    ) : (
      content
    );
  };

  const renderExpandedContent = (row: Row<DataTableFeatures, CatalogItem>) => {
    const code = row.original.Code;
    return (
      <PeriodDetailPanel
        columnVisibility={table.state.columnVisibility}
        isInDashboard={isInDashboard}
        periods={periodGroups.get(code) ?? []}
        renderFactsheetButton={renderFactsheetButton}
        showAll={fullyExpandedCodes.has(code)}
        onShowAll={() => onShowAll(code)}
        onViewInForwardCurves={
          !isForwardCurves && !isInDashboard && onViewInOtherCatalog
            ? () => onViewInOtherCatalog(code)
            : undefined
        }
      />
    );
  };

  const renderRowFooter = (row: Row<DataTableFeatures, CatalogItem>) => {
    if (!isForwardCurves || isInDashboard || !hasCodeFilter || !onViewInOtherCatalog) return null;
    return (
      <ViewInOtherCatalogLink
        label='View in Spot'
        onClick={() => onViewInOtherCatalog(row.original.Code)}
      />
    );
  };

  return (
    <DataTableVirtualized
      emptyContent='Nothing to display here. Try changing your filters.'
      estimateSize={
        isForwardCurves && !isInDashboard && hasCodeFilter
          ? COLLAPSED_ROW_HEIGHT + VIEW_IN_SPOT_LINK_HEIGHT
          : COLLAPSED_ROW_HEIGHT
      }
      getRowPresentation={getRowPresentation}
      hasRowFooter={() =>
        Boolean(isForwardCurves && !isInDashboard && hasCodeFilter && onViewInOtherCatalog)
      }
      onRowActivate={onRowActivate}
      renderExpandedContent={renderExpandedContent}
      renderHeader={renderHeader}
      renderRowFooter={renderRowFooter}
      rows={rows}
      table={table}
    />
  );
};

export default CatalogTableGrid;
